/**
 * useActivityRefresh - Proaktiv token refresh baserat på användaraktivitet
 * 
 * Lyssnar på användaraktivitet (mouse, keyboard, scroll) och refreshar
 * access token automatiskt för att förhindra utloggning under aktiv användning.
 */

import { useEffect, useRef, useCallback } from 'react';
import Cookies from 'js-cookie';
import api from '@/services/api/client';
import { useAuthStore } from '@/store/authStore';

// Konfigurerbart intervall för refresh (i millisekunder)
const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minuter (token är 15 min)
const ACTIVITY_DEBOUNCE = 30 * 1000; // 30 sekunder mellan refresh-försök
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minuter inaktivitet = logga ut

export function useActivityRefresh() {
  const lastRefreshRef = useRef<number>(Date.now());
  const lastActivityRef = useRef<number>(Date.now());
  const refreshIntervalRef = useRef<number | null>(null);
  const inactivityTimeoutRef = useRef<number | null>(null);
  const { user, logOutUser } = useAuthStore();

  // Refresh token-funktion
  const refreshToken = useCallback(async () => {
    try {
      const refreshTokenCookie = Cookies.get('refreshToken');
      
      if (!refreshTokenCookie) {
        console.warn('[ActivityRefresh] No refresh token found');
        return false;
      }

      const response = await api.post('/api/auth/refresh', {
        refreshToken: refreshTokenCookie,
      });

      if (response.data.success) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        
        // Uppdatera cookies
        Cookies.set('accessToken', accessToken, { expires: 7 });
        Cookies.set('refreshToken', newRefreshToken, { expires: 30 });

        // Uppdatera Zustand store
        useAuthStore.setState({
          accessToken,
          refreshToken: newRefreshToken,
        });

        lastRefreshRef.current = Date.now();
        if (import.meta.env.DEV) console.log('[ActivityRefresh] Token refreshed successfully');
        return true;
      }
    } catch (error) {
      // Ogiltig/utgången refresh token – rensa och skicka till inloggning (undvik retry-loop)
      console.warn('[ActivityRefresh] Refresh failed, clearing session and redirecting to login');
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
      logOutUser();
      window.location.href = '/login';
    }
    return false;
  }, [logOutUser]);

  // Hantera användaraktivitet
  const handleActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    // Återställ inaktivitetstimeout
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    
    inactivityTimeoutRef.current = window.setTimeout(() => {
      if (import.meta.env.DEV) console.log('[ActivityRefresh] User inactive for 30 minutes, logging out');
      logOutUser();
      window.location.href = '/login';
    }, INACTIVITY_TIMEOUT);

    // Kolla om vi behöver refresha token (debounced)
    const timeSinceLastRefresh = now - lastRefreshRef.current;
    
    if (timeSinceLastRefresh >= ACTIVITY_DEBOUNCE) {
      // Kolla om access token snart går ut
      const accessToken = Cookies.get('accessToken');
      if (accessToken) {
        try {
          // Försök dekoda token för att kolla expiry
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          const expiresAt = payload.exp * 1000; // Convert to ms
          const timeUntilExpiry = expiresAt - now;
          
          // Om token går ut inom 5 minuter, refresha nu
          if (timeUntilExpiry < 5 * 60 * 1000) {
            if (import.meta.env.DEV) console.log('[ActivityRefresh] Token expiring soon, refreshing...');
            refreshToken();
          }
        } catch {
          // Om vi inte kan dekoda token, försök refresha ändå
          if (timeSinceLastRefresh >= REFRESH_INTERVAL) {
            refreshToken();
          }
        }
      }
    }
  }, [refreshToken, logOutUser]);

  useEffect(() => {
    // Bara aktivera om användaren är inloggad
    if (!user) return;

    // Registrera event listeners för användaraktivitet
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    // Throttled activity handler (max 1 per sekund) – number i webbläsare, NodeJS.Timeout på server
    let throttleTimeout: number | null = null;
    const throttledActivity = () => {
      if (throttleTimeout) return;
      throttleTimeout = window.setTimeout(() => {
        throttleTimeout = null;
        handleActivity();
      }, 1000);
    };

    events.forEach(event => {
      window.addEventListener(event, throttledActivity, { passive: true });
    });

    // Initial aktivitet
    handleActivity();

    // Periodisk kontroll varje minut
    refreshIntervalRef.current = window.setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      
      // Om användaren varit aktiv senaste 5 minuterna, refresha om nödvändigt
      if (timeSinceLastActivity < 5 * 60 * 1000) {
        handleActivity();
      }
    }, 60 * 1000);

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledActivity);
      });
      
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, [user, handleActivity]);

  return {
    refreshToken,
    lastActivity: lastActivityRef.current,
  };
}
